import { useEffect, useRef, useState, type RefObject } from 'react'

/**
 * Bewegung auf den öffentlichen Seiten.
 *
 * Alles hier ist absichtlich klein: vier Haken und kein Framework. Eine
 * Animationsbibliothek wiegt mehr als die ganze Startseite, und nichts von
 * dem, was diese Seiten brauchen, geht nicht in CSS.
 *
 * Eine Regel gilt überall: **niemand verliert Inhalt, wenn die Bewegung
 * ausbleibt.** Ohne JavaScript, mit abgeschalteter Bewegung oder in einem
 * Browser ohne IntersectionObserver steht alles da, nur ohne Auftritt. Ein
 * Abschnitt, der auf `opacity: 0` wartet, ist die häufigste Art, eine Seite
 * versehentlich leer auszuliefern – und genau so sieht sie dann im
 * Vorschaubild und für Suchmaschinen aus.
 */

/** Ab dieser Breite ist Platz für alles auf einmal. */
export function useBreit(ab = 1041): boolean {
  const [breit, setBreit] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(min-width: ${ab}px)`).matches
  )
  useEffect(() => {
    const m = window.matchMedia(`(min-width: ${ab}px)`)
    const melden = (): void => setBreit(m.matches)
    melden()
    m.addEventListener('change', melden)
    return () => m.removeEventListener('change', melden)
  }, [ab])
  return breit
}

/** Hat der Besucher Bewegung abbestellt? */
export function useRuhig(): boolean {
  const [ruhig, setRuhig] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)')
    const melden = (): void => setRuhig(m.matches)
    melden()
    m.addEventListener('change', melden)
    return () => m.removeEventListener('change', melden)
  }, [])
  return ruhig
}

/**
 * Auftritt beim Hereinscrollen.
 *
 * Der Haken setzt `data-auftritt` auf dem Element – erst «warte», dann «da».
 * Ohne JavaScript steht dort gar nichts, und die CSS-Regel ohne Attribut ist
 * die sichtbare. Das ist die richtige Reihenfolge: das Unsichtbare wird
 * eingeschaltet, nicht das Sichtbare vorausgesetzt.
 */
export function useAuftritt<T extends HTMLElement>(): RefObject<T> {
  const ref = useRef<T>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return
    }
    el.dataset.auftritt = 'warte'
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        for (const e of eintraege) {
          if (e.isIntersecting) {
            ;(e.target as HTMLElement).dataset.auftritt = 'da'
            beobachter.unobserve(e.target)
          }
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    )
    beobachter.observe(el)
    return () => beobachter.disconnect()
  }, [])
  return ref
}

/**
 * Eine Zahl, die beim ersten Blick hochzählt.
 *
 * Sie startet nicht bei null, sondern bei gut der Hälfte: von null hoch zu
 * zählen dauert gefühlt ewig, und in der Zeit steht auf der Seite eine Zahl,
 * die nicht stimmt. Der Endwert ist der einzige, der lange genug steht, um
 * gelesen zu werden.
 */
export function useZaehler<T extends HTMLElement = HTMLElement>(
  ziel: number,
  dauer = 900
): [number, RefObject<T>] {
  const ref = useRef<T>(null)
  const [wert, setWert] = useState(ziel)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return
    }
    let bild = 0
    const start = Math.round(ziel * 0.55)
    setWert(start)
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        if (!eintraege.some((e) => e.isIntersecting)) return
        beobachter.disconnect()
        const t0 = performance.now()
        const schritt = (jetzt: number): void => {
          const u = Math.min(1, (jetzt - t0) / dauer)
          /* Weich auslaufen: die letzte Zahl soll nicht anspringen. */
          const w = 1 - Math.pow(1 - u, 3)
          setWert(Math.round(start + (ziel - start) * w))
          if (u < 1) bild = requestAnimationFrame(schritt)
        }
        bild = requestAnimationFrame(schritt)
      },
      { threshold: 0.4 }
    )
    beobachter.observe(el)
    return () => {
      beobachter.disconnect()
      cancelAnimationFrame(bild)
    }
  }, [ziel, dauer])
  return [wert, ref]
}

/**
 * Das Licht unter dem Zeiger.
 *
 * Ein einziger Zuhörer auf dem Raster, nicht einer je Kachel: bei fünfzehn
 * Kacheln wären das fünfzehn Ereignisse je Mausbewegung. Die Position geht
 * als CSS-Variable weiter, die Darstellung macht das Stylesheet.
 */
export function zeigerLicht(e: React.MouseEvent<HTMLElement>): void {
  const el = e.currentTarget
  const r = el.getBoundingClientRect()
  el.style.setProperty('--mx', `${e.clientX - r.left}px`)
  el.style.setProperty('--my', `${e.clientY - r.top}px`)
}

/*
  Auftritt und Glanz für eine ganze Seite.

  `useAuftritt` hängt an einem einzelnen Element. Auf den Glasseiten –
  Preise, FAQ, Rechtstexte, Kontakt, Marke – stehen zehn bis dreissig
  Flächen, und dreissig Referenzen von Hand zu setzen wäre dreissig
  Gelegenheiten, eine zu vergessen. Dieser Haken hängt EINMAL an der Wurzel
  und kümmert sich um alles darin.

  Zwei Wirkungen aus einer Hand:

    Auftritt – jede Fläche blendet beim Hereinscrollen auf. Sichtbar ist am
    Anfang, was schon im Bild steht; nur was darunter liegt, wartet. Eine
    Seite, deren oberer Teil auf ein Ereignis wartet, ist eine Seite, die
    beim ersten Blick leer ist.

    Glanz – ein weiches Licht folgt dem Zeiger über die Fläche, unter der er
    gerade steht. Ein einziger Horcher an der Wurzel statt einer je Karte;
    die Karte selbst zeichnet das Licht in ihrem `::after`.

  Bei «prefers-reduced-motion» passiert nichts von beidem – dann steht alles
  sofort und still da.
*/
const AUFTRITT_ZIELE =
  '.rp-glas, .rp-abschnitt, .rp-preis-kopf, .rp-schluss, .rp-vergleich-rahmen, .rp-testphase'
const GLANZ_ZIELE = '.rp-glas, .rp-paket, .rp-vergleich-rahmen, .rp-testphase'

export function useSeitenleben<T extends HTMLElement>(): RefObject<T> {
  const ref = useRef<T>(null)
  useEffect(() => {
    const wurzel = ref.current
    if (!wurzel) return
    const ruhig =
      typeof window === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    /* ---- Glanz: ein Horcher für die ganze Seite ---- */
    const bewegen = (e: PointerEvent): void => {
      const ziel = (e.target as HTMLElement | null)?.closest<HTMLElement>(GLANZ_ZIELE)
      if (!ziel) return
      const r = ziel.getBoundingClientRect()
      ziel.style.setProperty('--mx', `${e.clientX - r.left}px`)
      ziel.style.setProperty('--my', `${e.clientY - r.top}px`)
    }
    if (!ruhig) wurzel.addEventListener('pointermove', bewegen, { passive: true })

    /* ---- Auftritt ---- */
    let beobachter: IntersectionObserver | null = null
    if (!ruhig && typeof IntersectionObserver !== 'undefined') {
      beobachter = new IntersectionObserver(
        (eintraege) => {
          for (const e of eintraege) {
            if (!e.isIntersecting) continue
            ;(e.target as HTMLElement).dataset.auftritt = 'da'
            beobachter?.unobserve(e.target)
          }
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.06 }
      )
      for (const el of Array.from(wurzel.querySelectorAll<HTMLElement>(AUFTRITT_ZIELE))) {
        /* Was beim Laden schon im Bild steht, wartet nicht. */
        if (el.getBoundingClientRect().top < window.innerHeight * 0.92) continue
        el.dataset.auftritt = 'warte'
        beobachter.observe(el)
      }
    }
    return () => {
      wurzel.removeEventListener('pointermove', bewegen)
      beobachter?.disconnect()
    }
  }, [])
  return ref
}

/*
  Wischen mit dem Finger.

  Die Karussells liessen sich bisher auf vier Wegen bedienen – Karte, Pfeile,
  Punkte, Pfeiltasten. Drei davon setzen einen Zeiger oder eine Tastatur
  voraus, und der vierte ist ein Tippziel von wenigen Pixeln. Auf dem Handy
  ist Wischen die naheliegende Geste, und sie fehlte schlicht.

  Wie es rechnet: bei jedem angefangenen `schwelle` an waagrechtem Weg rückt
  es eine Karte weiter. Nicht erst beim Loslassen – so folgt die Schau der
  Hand, statt nach der Geste einen Sprung zu machen.

  Zwei Dinge, die eine Wischgeste sonst kaputt machen:

    Das Blättern der Seite. Solange nicht feststeht, ob die Geste waagrecht
    oder senkrecht gemeint ist, greift der Haken nicht ein. Erst wenn der
    waagrechte Weg den senkrechten übersteigt, übernimmt er – und dann sagt
    `touch-action: pan-y` im Stilblatt dem Browser, dass er die Waagrechte
    uns überlässt und die Senkrechte behält.

    Der Klick danach. Wer über eine Karte wischt, löst beim Loslassen einen
    Klick auf ihr aus und landet ungewollt bei einer anderen Branche. Nach
    einer erkannten Geste wird der nächste Klick deshalb in der
    Erfassungsphase abgefangen.
*/
export function useWisch(
  ziel: RefObject<HTMLElement>,
  aufSchritt: (richtung: 1 | -1) => void,
  schwelle = 52
): void {
  /*
    Der Rückruf liegt in einer Referenz und NICHT in den Abhängigkeiten.

    Sonst hängt der Horcher an seiner Identität: gibt die aufrufende Seite
    eine Pfeilfunktion mit (`aufAktiv={(i) => setX(i)}`), ist sie bei jedem
    Rendern eine neue, der Effekt läuft neu, und die Horcher werden mitten in
    der Geste abgemeldet und neu angemeldet. Mit ihnen gehen Startpunkt und
    Zeigernummer verloren.

    Gemessen: in der Branchenschau rückte ein Wisch über 170 Pixel um eine
    Karte, im Gewerbekreis um drei – gleicher Haken, gleicher Schwellenwert,
    einziger Unterschied die Pfeilfunktion.
  */
  const schritt = useRef(aufSchritt)
  schritt.current = aufSchritt

  useEffect(() => {
    const el = ziel.current
    if (!el) return

    let zeiger = -1
    let x0 = 0
    let y0 = 0
    let entschieden = false
    let quer = false
    let gewischt = false

    const runter = (e: PointerEvent): void => {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      zeiger = e.pointerId
      x0 = e.clientX
      y0 = e.clientY
      entschieden = false
      quer = false
      gewischt = false
    }

    const bewegen = (e: PointerEvent): void => {
      if (e.pointerId !== zeiger) return
      let dx = e.clientX - x0
      const dy = e.clientY - y0

      if (!entschieden) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return
        entschieden = true
        quer = Math.abs(dx) > Math.abs(dy)
        if (!quer) {
          /* Senkrecht gemeint – die Seite darf blättern, wir halten still. */
          zeiger = -1
          return
        }
        el.setPointerCapture?.(e.pointerId)
      }
      if (!quer) return

      while (Math.abs(dx) >= schwelle) {
        schritt.current(dx > 0 ? -1 : 1)
        gewischt = true
        x0 += Math.sign(dx) * schwelle
        dx = e.clientX - x0
      }
      if (e.cancelable) e.preventDefault()
    }

    const hoch = (e: PointerEvent): void => {
      if (e.pointerId !== zeiger) return
      zeiger = -1
      if (gewischt) {
        const schlucken = (k: Event): void => {
          k.stopPropagation()
          k.preventDefault()
        }
        el.addEventListener('click', schlucken, { capture: true, once: true })
        /* Bleibt der Klick aus – etwa weil der Finger neben einer Karte
           losgelassen wurde –, darf der Horcher nicht liegen bleiben. */
        setTimeout(() => el.removeEventListener('click', schlucken, true), 350)
      }
    }

    el.addEventListener('pointerdown', runter)
    el.addEventListener('pointermove', bewegen, { passive: false })
    el.addEventListener('pointerup', hoch)
    el.addEventListener('pointercancel', hoch)
    return () => {
      el.removeEventListener('pointerdown', runter)
      el.removeEventListener('pointermove', bewegen)
      el.removeEventListener('pointerup', hoch)
      el.removeEventListener('pointercancel', hoch)
    }
  }, [ziel, schwelle])
}
